import React from "react";

import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  alpha,
} from "@mui/material";

const TestimonialsSection = ({ themeColors = {} }) => {
  // Define local constants with fallbacks for themeColors properties

  const textPrimary = themeColors.textPrimary || "#1a1a1a";

  const textSecondary = themeColors.textSecondary || "rgba(26, 26, 26, 0.7)";

  const testimonials = [
    {
      name: "Jay Patel",

      location: "Melbourne",

      role: "Verified Seller",

      quote:
        "ScrapTrade helped me sell 2 tons of copper in a single day. Incredible efficiency!",

      color: "#F4C542", // Primary color accent
    },

    {
      name: "Priya Malhotra",

      location: "Delhi",

      role: "Verified Seller",

      quote:
        "Got the best aluminum price instantly. Transparent and reliable service!",

      color: "#2D3A27", // Secondary color accent
    },

    {
      name: "Ahmed Khan",

      location: "Hyderabad",

      role: "Premium Buyer",

      quote: "Bid process is smooth. Bought in bulk with confidence!",

      color: "#F4C542", // Primary color accent
    },

    {
      name: "Sara Lee",

      location: "Sydney",

      role: "Verified Buyer",

      quote: "I trust ScrapTrade for scrap sourcing now. Amazing UX!",

      color: "#2D3A27", // Secondary color accent
    },
  ];

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",

        py: { xs: 8, md: 12 },

        position: "relative",

        overflow: "hidden",
      }}
    >
      {/* Background Elements */}

      <Box
        sx={{
          position: "absolute",

          top: "-50%",

          right: "-20%",

          width: "600px",

          height: "600px",

          background: `radial-gradient(circle, ${alpha(
            themeColors.primary,
            0.1
          )} 0%, transparent 70%)`,

          borderRadius: "50%",

          filter: "blur(100px)",
        }}
      />

      <Box
        sx={{
          position: "absolute",

          bottom: "-30%",

          left: "-10%",

          width: "400px",

          height: "400px",

          background: `radial-gradient(circle, ${alpha(
            themeColors.secondary,
            0.08
          )} 0%, transparent 70%)`,

          borderRadius: "50%",

          filter: "blur(80px)",
        }}
      />

      {/* Main Content Container with correct padding */}

      <Container sx={{ position: "relative", zIndex: 2, overflow: "hidden" }}>
        {/* Section Header */}

        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{
              mb: 2,

              fontSize: { xs: "2rem", md: "2.5rem" },

              color: textPrimary,

              letterSpacing: "-1px",
            }}
          >
            What Our Users Say
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: textSecondary,

              fontWeight: 400,

              maxWidth: "500px",

              mx: "auto",
            }}
          >
            Hear directly from our valued buyers and sellers about their
            ScrapTrade experience.
          </Typography>
        </Box>

        {/* Testimonial Carousel Container */}

        <Box
          sx={{
            display: "flex",

            py: 2,

            px: { xs: 2, md: 4 }, // Added horizontal padding to the main carousel container

            animation: "scroll-left 20s linear infinite",

            "&:hover": {
              animationPlayState: "paused",
            },

            "@keyframes scroll-left": {
              "0%": { transform: "translateX(0)" },

              "100%": { transform: `translateX(-50%)` },
            },
          }}
        >
          {/* Duplicate testimonials for infinite loop effect */}

          {[...testimonials, ...testimonials].map((testimonial, index) => (
            <Box
              key={index}
              sx={{
                flexShrink: 0, // Prevent items from shrinking

                width: { xs: "90%", sm: "50%", md: "33.33%", lg: "26.5%" }, // Adjust width for responsiveness

                px: { xs: 1.5, md: 2 }, // Horizontal padding between cards

                display: "flex",

                justifyContent: "center",

                alignItems: "stretch", // Ensure cards stretch to fill height
              }}
            >
              <Card
                sx={{
                  background: `linear-gradient(145deg, ${alpha(
                    testimonial.color,
                    0.05
                  )} 0%, ${alpha(themeColors.backgroundLight, 0.95)} 100%)`, // Gradient background with accent color

                  borderRadius: 4,

                  border: `1px solid ${alpha(testimonial.color, 0.2)}`, // Border with accent color

                  position: "relative",

                  overflow: "hidden",

                  transition: "all 0.3s ease-in-out",

                  width: "100%",

                  display: "flex",

                  flexDirection: "column",

                  justifyContent: "space-between",

                  boxShadow: `0 4px 15px ${alpha(testimonial.color, 0.1)}`, // Shadow with accent color

                  "&:hover": {
                    transform: "translateY(-6px)",

                    boxShadow: `0 10px 30px ${alpha(testimonial.color, 0.2)}`,
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                  {/* Quote Mark as Background Element */}

                  <Box
                    sx={{
                      position: "absolute",

                      top: { xs: "10px", md: "15px" },

                      left: { xs: "15px", md: "20px" },

                      fontSize: { xs: "4rem", md: "5rem" },

                      color: alpha(testimonial.color, 0.15), // Subtle quote mark with accent color

                      zIndex: 0,

                      lineHeight: 1,

                      fontWeight: 900,

                      userSelect: "none",
                    }}
                  >
                    “
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,

                      color: themeColors.textPrimary,

                      lineHeight: 1.6,

                      fontSize: { xs: "0.9rem", md: "1rem" },

                      textAlign: "left",

                      mb: 3,

                      position: "relative",

                      zIndex: 1,
                    }}
                  >
                    {testimonial.quote}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{
                      mt: 3,
                      pt: 2,
                      borderTop: `1px dashed ${alpha(
                        themeColors.textSecondary,
                        0.1
                      )}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 45,

                        height: 45,

                        borderRadius: "50%",

                        background: `linear-gradient(135deg, ${
                          testimonial.color
                        } 0%, ${alpha(testimonial.color, 0.8)} 100%)`, // Avatar background with accent color

                        display: "flex",

                        alignItems: "center",

                        justifyContent: "center",

                        flexShrink: 0,

                        boxShadow: `0 2px 8px ${alpha(
                          testimonial.color,
                          0.15
                        )}`,

                        color: "#fff",

                        fontWeight: 600,

                        fontSize: "0.9rem",
                      }}
                    >
                      {testimonial.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </Box>

                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ color: themeColors.textPrimary }}
                      >
                        {testimonial.name}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{ color: themeColors.textSecondary }}
                      >
                        {testimonial.location} • {testimonial.role}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;
